import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import { GET_DURATION_TYPE_DISTRIBUTION_CHART_DATA_API } from "../../../../services/ApiCalls";
import { useTranslation } from "react-i18next";

const ReturningChart = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("thisMonth");

  const periodTabs = [
    { key: "today", label: t("returningChart.tabs.today") },
    { key: "yesterday", label: t("returningChart.tabs.yesterday") },
    { key: "thisWeek", label: t("returningChart.tabs.thisWeek") },
    { key: "thisMonth", label: t("returningChart.tabs.thisMonth") },
    { key: "lastMonth", label: t("returningChart.tabs.lastMonth") },
    { key: "custom", label: t("returningChart.tabs.custom") },
  ];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState(false);

  const fetchDurationTypedistributionGraphData = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const res = await GET_DURATION_TYPE_DISTRIBUTION_CHART_DATA_API(payload);
      if (res?.data?.status === "success") {
        setData(res?.data?.data?.periods || {});
      } else {
        console.error("Error:", res?.data?.message || "Failed to fetch");
      }
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCallApiOnDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setDateError(true);
        toast.error(t("returningChart.errors.invalidDateRange"));
        return;
      }

      setDateError(false);
      setActiveTab("custom");
      fetchDurationTypedistributionGraphData();
    }
  };

  useEffect(() => {
    fetchDurationTypedistributionGraphData();
  }, []);

  const { series, options, bookingTypes } = useMemo(() => {
    const selectedData = data?.[activeTab] || {};

    const timeSlots = Object.entries(selectedData || {}).map(
      ([slot, values]) => ({
        label: slot,
        newCustomers: values?.newCustomers || 0,
        returningCustomers: values?.returningCustomers || 0,
      })
    );

    const total = timeSlots.reduce(
      (sum, item) => sum + item.newCustomers + item.returningCustomers,
      0
    );

    const enhancedSlots = timeSlots.map((item) => {
      const totalSlot = item.newCustomers + item.returningCustomers;
      const percentage = total
        ? ((totalSlot / total) * 100).toFixed(1)
        : 0;
      return { ...item, totalSlot, percentage: parseFloat(percentage) };
    });

    const hasValidData = enhancedSlots.some((item) => item.totalSlot > 0);

    let seriesArr = [];
    let labelsArr = [];
    let colorsArr = [];

    if (hasValidData) {
      seriesArr = enhancedSlots.map((item) => item.percentage);
      labelsArr = enhancedSlots.map((item) => item.label);
      colorsArr = [
        "#e8307a",
        "#ff6b6b",
        "#71da61",
        "#4c5eff",
        "#f3b700",
        "#009298",
        "#003e70",
      ];
    } else {
      seriesArr = [100];
      labelsArr = [t("returningChart.noData")];
      colorsArr = ["#d3d3d3"];
    }

    return {
      series: seriesArr,
      bookingTypes: hasValidData
        ? enhancedSlots.map((item, i) => ({
          type: item.label,
          newCustomers: item.newCustomers,
          returningCustomers: item.returningCustomers,
          percentage: item.percentage,
          color: colorsArr[i % colorsArr.length],
        }))
        : [
          {
            type: t("returningChart.noData"),
            newCustomers: 0,
            returningCustomers: 0,
            percentage: 0,
            color: "#d3d3d3",
          },
        ],
      options: {
        chart: { type: "donut", height: 300 },
        labels: labelsArr,
        colors: colorsArr,
        legend: {
          show: true,
          position: "bottom",
          fontSize: "12px",
          markers: { width: 10, height: 10, radius: 50 },
        },
        stroke: { width: 2, colors: ["#fff"] },
        plotOptions: {
          pie: {
            donut: { size: "60%" },
          },
        },
        tooltip: {
          y: {
            formatter: (val, opts) => {
              const label = opts?.w?.globals?.labels?.[opts?.seriesIndex];
              const match = enhancedSlots.find((s) => s.label === label);
              if (match) {
                return (
                  `New Customers: ${match.newCustomers}\n` +
                  `Returning Customers: ${match.returningCustomers}\n` +
                  `Percentage: ${match.percentage}%`
                );
              }
              return val;
            },
          },
        },
        dataLabels: { enabled: false },
      },
    };
  }, [data, activeTab, t]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <div style={{ width: "150px" }}>
          <label className="small fw-semibold">
            {t("returningChart.filters.startDate")}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>

        <div style={{ width: "150px" }}>
          <label className="small fw-semibold">
            {t("returningChart.filters.endDate")}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>

        <button
          className="btn btn-primary px-4"
          onClick={handleCallApiOnDateChange}
          disabled={!startDate || !endDate || loading}
        >
          {loading
            ? t("returningChart.actions.loading")
            : t("returningChart.actions.apply")}
        </button>
      </div>

      <div className="chartCard">
        <h6 className="fw-bold mb-2" style={{ color: "#333" }}>
          {t("returningChart.title")}
        </h6>

        <div className="d-flex gap-3 mb-3 flex-wrap">
          {periodTabs.map((tab) => (
            <span
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: activeTab === tab.key ? "bold" : "normal",
                color: activeTab === tab.key ? "#e8307a" : "#6c757d",
                borderBottom:
                  activeTab === tab.key ? "2px solid #e8307a" : "none",
              }}
            >
              {tab.label}
            </span>
          ))}
        </div>

        <div className="row align-items-center">
          <div className="col-md-6">
            <ReactApexChart
              key={activeTab + series.join("-")}
              options={options}
              series={series}
              type="donut"
              height={260}
            />
          </div>

          <div className="col-md-6">
            {bookingTypes.map((booking) => (
              <div
                key={booking.type}
                className="d-flex align-items-center mb-3"
                title={`${booking.type}\nNew Customers: ${booking.newCustomers}\nReturning Customers: ${booking.returningCustomers}\nPercentage: ${booking.percentage}%`}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: booking.color,
                    display: "inline-block",
                    marginRight: "8px",
                  }}
                ></span>
                <span className="fw-bold me-auto" style={{ fontSize: "14px" }}>
                  {booking.type} ({booking.newCustomers} /{" "}
                  {booking.returningCustomers})
                </span>
                <span
                  className="fw-bold"
                  style={{ fontSize: "14px", color: booking.color }}
                >
                  {booking.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReturningChart;
